import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Code, Copy, CheckCircle, Globe, Info, Eye, CaretDown } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmbedPage = ({ user, setUser }) => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
        if (data.length > 0) {
          setSelectedSite(data[0]);
        }
      }
    } catch (error) {
      toast.error("Kunne ikke hente sites");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success("Kopieret til udklipsholder");
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      toast.error("Kunne ikke kopiere");
    }
  };

  // Get the widget host URL - use site's configured URL or show placeholder
  const getWidgetHostUrl = () => {
    if (selectedSite?.widget_host_url) {
      return selectedSite.widget_host_url;
    }
    return 'https://DIN-PRODUKTIONS-URL';
  };

  const getEmbedScript = () => {
    if (!selectedSite) return '';
    const hostUrl = getWidgetHostUrl();
    const isPlaceholder = !selectedSite?.widget_host_url;
    
    return `<!-- GardinBot Chat Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${hostUrl}/widget.js';
    script.async = true;
    script.dataset.siteKey = '${selectedSite.site_key}';
    document.head.appendChild(script);
  })();
</script>${isPlaceholder ? '\n\n<!-- VIGTIGT: Erstat DIN-PRODUKTIONS-URL med din faktiske produktions-URL -->' : ''}`;
  };

  const getReactCode = () => {
    if (!selectedSite) return '';
    const hostUrl = getWidgetHostUrl();
    return `import { useEffect, useState } from 'react';

// GardinBot Chat Widget Component
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const SITE_KEY = '${selectedSite.site_key}';
  const API_URL = '${hostUrl}/api';

  // Implement your chat widget UI here
  // Use fetch to call:
  // - GET \${API_URL}/chat/config?site=\${SITE_KEY}
  // - POST \${API_URL}/chat/message
  // - POST \${API_URL}/chat/handoff

  return (
    <div className="fixed bottom-6 right-6">
      {/* Your widget implementation */}
    </div>
  );
};

export default ChatWidget;`;
  };

  const getApiEndpoints = () => {
    const hostUrl = getWidgetHostUrl();
    return `# GardinBot API Endpoints

## Get Chat Configuration
GET ${hostUrl}/api/chat/config?site=${selectedSite?.site_key || 'YOUR_SITE_KEY'}

Response:
{
  "site_key": "${selectedSite?.site_key || 'site_key'}",
  "brand_name": "${selectedSite?.brand_name || 'Brand'}",
  "welcome_message": "Hej! Hvordan kan jeg hjælpe?",
  "widget_header_color": "${selectedSite?.widget_header_color || '#4338CA'}",
  "widget_bubble_color": "${selectedSite?.widget_bubble_color || '#4F46E5'}",
  ...
}

## Send Message
POST ${hostUrl}/api/chat/message
Content-Type: application/json

{
  "site_key": "${selectedSite?.site_key || 'YOUR_SITE_KEY'}",
  "session_id": "unique_session_id",
  "user_message": "Brugerens besked",
  "page_url": "https://example.com/page"
}

Response:
{
  "answer": "AI svar...",
  "confidence_score": 0.85,
  "handoff_needed": false,
  "sources": [...]
}

## Create Handoff
POST ${hostUrl}/api/chat/handoff
Content-Type: application/json

{
  "site_key": "${selectedSite?.site_key || 'YOUR_SITE_KEY'}",
  "session_id": "session_id",
  "customer_email": "kunde@example.com",
  "customer_name": "Kundens Navn",
  "issue_summary": "Telefon: 12345678 | Beskrivelse..."
}`;
  };

  const CodeBlock = ({ code, language, copyId }) => (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <Button
        variant="secondary"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => handleCopy(code, copyId)}
      >
        {copied === copyId ? (
          <><CheckCircle className="w-4 h-4 mr-1 text-emerald-500" />Kopieret</>
        ) : (
          <><Copy className="w-4 h-4 mr-1" />Kopier</>
        )}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="p-6 md:p-8 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="embed-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Embed Chat Widget
          </h1>
          <p className="text-slate-600">Integrer chat-widgetten på din hjemmeside</p>
        </div>

        {sites.length === 0 ? (
          <Card className="card-default border-amber-200 bg-amber-50/30">
            <CardContent className="p-6 text-center">
              <Globe className="w-12 h-12 text-amber-500 mx-auto mb-4" weight="duotone" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Ingen sites konfigureret</h2>
              <p className="text-slate-600 mb-4">Opret først en site under "Sites" menuen.</p>
              <Button onClick={() => window.location.href = '/sites'}>
                Gå til Sites
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Site selector */}
            <Card className="card-default mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Globe className="w-5 h-5 text-indigo-600" weight="duotone" />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-slate-700">Vælg site</label>
                    <Select 
                      value={selectedSite?.site_key} 
                      onValueChange={(key) => setSelectedSite(sites.find(s => s.site_key === key))}
                    >
                      <SelectTrigger className="mt-1" data-testid="site-selector">
                        <SelectValue placeholder="Vælg site" />
                      </SelectTrigger>
                      <SelectContent>
                        {sites.map(site => (
                          <SelectItem key={site.site_key} value={site.site_key}>
                            {site.brand_name} ({site.domain})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedSite && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Site Key</p>
                      <code className="text-sm font-mono text-indigo-600">{selectedSite.site_key}</code>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Embed options */}
            <Tabs defaultValue="script" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="script" data-testid="tab-script">
                  <Code className="w-4 h-4 mr-2" />
                  Script Tag
                </TabsTrigger>
                <TabsTrigger value="react" data-testid="tab-react">
                  <Code className="w-4 h-4 mr-2" />
                  React
                </TabsTrigger>
                <TabsTrigger value="api" data-testid="tab-api">
                  <Code className="w-4 h-4 mr-2" />
                  API
                </TabsTrigger>
              </TabsList>

              <TabsContent value="script">
                <Card className="card-default">
                  <CardHeader>
                    <CardTitle>Script Tag Integration</CardTitle>
                    <CardDescription>
                      Den nemmeste måde at integrere chat-widgetten. Kopier koden og indsæt den før &lt;/body&gt; på din hjemmeside.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!selectedSite?.widget_host_url && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
                          <div className="text-sm text-slate-700">
                            <p className="font-medium mb-1">Manglende produktions-URL</p>
                            <p className="text-slate-600">
                              Gå til <strong>Sites → {selectedSite?.brand_name} → Widget Udseende</strong> og udfyld "Widget Produktions-URL" 
                              med din faktiske produktions-URL (f.eks. <code className="bg-white px-1 rounded">https://chat.gardinshoppen.dk</code>).
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <CodeBlock code={getEmbedScript()} language="html" copyId="script" />
                    
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" weight="fill" />
                        <div className="text-sm text-slate-700">
                          <p className="font-medium mb-1">Installation</p>
                          <ol className="list-decimal list-inside space-y-1 text-slate-600">
                            <li>Kopier koden ovenfor</li>
                            <li>Indsæt den lige før <code className="bg-white px-1 rounded">&lt;/body&gt;</code> tagget på din hjemmeside</li>
                            <li>Widgetten vil automatisk blive vist i nederste højre hjørne</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="react">
                <Card className="card-default">
                  <CardHeader>
                    <CardTitle>React Integration</CardTitle>
                    <CardDescription>
                      Byg din egen custom widget i React ved at bruge vores API endpoints.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CodeBlock code={getReactCode()} language="jsx" copyId="react" />
                    
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" weight="fill" />
                        <div className="text-sm text-slate-600">
                          <p>Dette er et udgangspunkt for en custom React implementation. 
                          Se "API" tabben for detaljer om de endpoints du skal bruge.</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="api">
                <Card className="card-default">
                  <CardHeader>
                    <CardTitle>API Documentation</CardTitle>
                    <CardDescription>
                      Brug vores REST API til at bygge en helt custom integration.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CodeBlock code={getApiEndpoints()} language="bash" copyId="api" />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-emerald-700">GET /chat/config</p>
                        <p className="text-xs text-emerald-600 mt-1">Hent widget konfiguration</p>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-700">POST /chat/message</p>
                        <p className="text-xs text-blue-600 mt-1">Send besked til AI</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-purple-700">POST /chat/handoff</p>
                        <p className="text-xs text-purple-600 mt-1">Opret kundeservice ticket</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Preview section */}
            {selectedSite && (
              <Card className="card-default mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-indigo-600" weight="duotone" />
                    Widget Preview
                  </CardTitle>
                  <CardDescription>
                    Se hvordan widgetten vil se ud med dine indstillinger
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-100 rounded-lg p-8 relative min-h-[200px]">
                    <div className="absolute bottom-4 right-4 flex flex-col items-end gap-3">
                      {/* Preview chat window */}
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-80 bg-white rounded-2xl shadow-xl overflow-hidden"
                      >
                        <div 
                          className="p-4 text-white"
                          style={{ backgroundColor: selectedSite.widget_header_color || '#4338CA' }}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                              </svg>
                            </div>
                            <div>
                              <h3 className="font-semibold">{selectedSite.brand_name}</h3>
                              <p className="text-xs opacity-80">Online nu</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[80%]">
                            <p className="text-sm text-slate-700">{selectedSite.welcome_message || 'Hej! Hvordan kan jeg hjælpe dig?'}</p>
                          </div>
                          <div 
                            className="rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] ml-auto text-white"
                            style={{ backgroundColor: selectedSite.widget_user_message_color || '#4F46E5' }}
                          >
                            <p className="text-sm">Hvad er leveringstiden?</p>
                          </div>
                        </div>
                      </motion.div>
                      
                      {/* Preview bubble */}
                      <div 
                        className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center cursor-pointer"
                        style={{ backgroundColor: selectedSite.widget_bubble_color || '#4F46E5' }}
                      >
                        <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                        </svg>
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-500 absolute bottom-4 left-4">
                      Position: {selectedSite.widget_position === 'left' ? 'Venstre' : 'Højre'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};
